/**
 * Top-level routing.
 *
 * Route tree:
 *
 *   /                         → redirect to /dashboard (RequireAuth will
 *                                 bounce unauthenticated or non-active users
 *                                 to the appropriate status page)
 *   /sign-in                  → SignInPage (public)
 *   /pending-approval         → PendingApprovalPage (auth'd + status=pending)
 *   /disabled                 → DisabledPage (auth'd + status=disabled)
 *   /dashboard                → DashboardPage (auth'd + status=active)
 *   /admin                    → AdminDashboardPage (auth'd + active + super admin)
 *   *                         → 404 (redirect to /)
 *
 * The `RequireAuth` guard handles the dance between sign-in, pending,
 * and disabled. The `allowPending` and `allowDisabled` props on the
 * /pending-approval and /disabled routes stop the guard from
 * redirecting those pages back to themselves in a loop.
 *
 * `/admin` uses `RequireAuth` to block unauthenticated/pending/disabled
 * users, then `RequireSuperAdmin` to block active-but-non-admin users.
 */

import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { TopBar } from "@/components/TopBar";
import { AboutPage } from "@/features/about/AboutPage";
import { EventsJournalPage } from "@/features/activities/EventsJournalPage";
import { AdminDashboardPage } from "@/features/admin/AdminDashboardPage";
import { DisabledPage } from "@/features/auth/DisabledPage";
import { PendingApprovalPage } from "@/features/auth/PendingApprovalPage";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { RequireFamily } from "@/features/auth/RequireFamily";
import { RequireSuperAdmin } from "@/features/auth/RequireSuperAdmin";
import { SignInPage } from "@/features/auth/SignInPage";
import { ChildrenListPage } from "@/features/children/ChildrenListPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { CreateFamilyPage } from "@/features/family/CreateFamilyPage";
import { FamilySettingsPage } from "@/features/family/FamilySettingsPage";
import { AcceptInvitationPage } from "@/features/invitations/AcceptInvitationPage";
import { ChildPerspectivesPage } from "@/features/perspectives/ChildPerspectivesPage";

export function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

function AppShell() {
  const location = useLocation();
  const isAboutPage = location.pathname === "/about";

  return (
    <div className="relative z-[3] min-h-screen flex flex-col font-sans text-ink">
      <TopBar />
      <div className="flex-1">
        <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route path="/sign-in" element={<SignInPage />} />

            <Route
              path="/pending-approval"
              element={
                <RequireAuth allowPending>
                  <PendingApprovalPage />
                </RequireAuth>
              }
            />

            <Route
              path="/disabled"
              element={
                <RequireAuth allowDisabled>
                  <DisabledPage />
                </RequireAuth>
              }
            />

            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <RequireFamily>
                    <DashboardPage />
                  </RequireFamily>
                </RequireAuth>
              }
            />

            <Route
              path="/family/create"
              element={
                <RequireAuth>
                  <CreateFamilyPage />
                </RequireAuth>
              }
            />

            <Route
              path="/family"
              element={
                <RequireAuth>
                  <RequireFamily>
                    <FamilySettingsPage />
                  </RequireFamily>
                </RequireAuth>
              }
            />

            <Route
              path="/children"
              element={
                <RequireAuth>
                  <RequireFamily>
                    <ChildrenListPage />
                  </RequireFamily>
                </RequireAuth>
              }
            />

            <Route
              path="/children/:childId/perspectives"
              element={
                <RequireAuth>
                  <RequireFamily>
                    <ChildPerspectivesPage />
                  </RequireFamily>
                </RequireAuth>
              }
            />

            <Route
              path="/events"
              element={
                <RequireAuth>
                  <RequireFamily>
                    <EventsJournalPage />
                  </RequireFamily>
                </RequireAuth>
              }
            />

            <Route path="/about" element={<AboutPage />} />

            <Route path="/invite/:token" element={<AcceptInvitationPage />} />

            <Route
              path="/admin/*"
              element={
                <RequireAuth>
                  <RequireSuperAdmin>
                    <AdminDashboardPage />
                  </RequireSuperAdmin>
                </RequireAuth>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>

        {!isAboutPage && (
          <footer className="border-t border-edge-soft px-6 md:px-11 py-6 flex items-center justify-between">
            <div className="font-sans text-[12px] text-ink-dim">
              Make It Count · Rogue Wolf Advanced Research LLC
            </div>
            <Link
              to="/about"
              className="font-sans text-[12px] text-ink-muted hover:text-coral-deep"
            >
              About · Research
            </Link>
          </footer>
        )}
      </div>
  );
}
